import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Rating } from '@mui/material';
import { 
  LocationOn as LocationIcon, 
  Phone as PhoneIcon, 
  Schedule as ScheduleIcon,
  Navigation as NavigationIcon 
} from '@mui/icons-material';

const GoogleMapComponent = ({ garages, center, onGarageSelect, formatDistance, formatPrice }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMaps = () => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps && window.google.maps.Map) {
        initializeMap();
        return;
      }

      // Check if script is already loading
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.google && window.google.maps && window.google.maps.Map) {
            initializeMap();
          }
        });
        return;
      }

      // Load the script
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        console.error('Google Maps API key not found in environment variables');
        setMapError(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        console.error('Failed to load Google Maps');
        setMapError(true);
      };

      // Create global callback
      window.initGoogleMaps = () => {
        try {
          if (window.google && window.google.maps && window.google.maps.Map) {
            initializeMap();
            setMapLoaded(true);
          } else {
            setMapError(true);
          }
        } catch (error) {
          console.error('Error initializing Google Maps:', error);
          setMapError(true);
        }
        delete window.initGoogleMaps; // Clean up
      };

      document.head.appendChild(script);
    };

    loadGoogleMaps();

    return () => {
      // Cleanup markers
      markersRef.current.forEach(marker => {
        if (marker.setMap) marker.setMap(null);
      });
      markersRef.current = [];
      
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, []);

  // Update markers when garages change
  useEffect(() => {
    if (mapInstanceRef.current && garages.length > 0) {
      updateMarkers();
    }
  }, [garages]);

  // Update map center when coordinates change
  useEffect(() => {
    if (mapInstanceRef.current && center && center.latitude && center.longitude) {
      mapInstanceRef.current.setCenter({ lat: center.latitude, lng: center.longitude });
    }
  }, [center]);

  const initializeMap = () => {
    try {
      if (!mapRef.current) {
        console.error('Map container not available');
        setMapError(true);
        return;
      }

      // Use provided center or default to UK center
      const mapCenter = center || { latitude: 52.5074, longitude: -1.1278 }; // UK center
      
      if (!mapCenter.latitude || !mapCenter.longitude) {
        console.error('Invalid center coordinates:', mapCenter);
        setMapError(true);
        return;
      }

      if (!window.google || !window.google.maps) {
        console.error('Google Maps API not loaded');
        setMapError(true);
        return;
      }

      const mapOptions = {
        zoom: 12,
        center: { lat: mapCenter.latitude, lng: mapCenter.longitude },
        mapId: '8523c34cba1ff6fe69bee7b5', // Required for Advanced Markers
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      };

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
    
      // Add user location marker using AdvancedMarkerElement (only if center is available)
      if (center && center.latitude && center.longitude) {
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          const userMarkerContent = document.createElement('div');
          userMarkerContent.innerHTML = `
            <div style="
              width: 24px; 
              height: 24px; 
              border-radius: 50%; 
              background: #1976d2; 
              border: 2px solid white; 
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            "></div>
          `;
          
          new window.google.maps.marker.AdvancedMarkerElement({
            position: { lat: center.latitude, lng: center.longitude },
            map: mapInstanceRef.current,
            title: 'Your location',
            content: userMarkerContent
          });
        } else {
          // Fallback to regular Marker if AdvancedMarkerElement not available
          new window.google.maps.Marker({
            position: { lat: center.latitude, lng: center.longitude },
            map: mapInstanceRef.current,
            title: 'Your location',
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="8" fill="#1976d2" stroke="white" stroke-width="2"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(24, 24),
              anchor: new window.google.maps.Point(12, 12)
            }
          });
        }
      }

      // Create info window
      infoWindowRef.current = new window.google.maps.InfoWindow();

      updateMarkers();
      setMapLoaded(true);
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError(true);
    }
  };

  const updateMarkers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker.setMap) marker.setMap(null);
    });
    markersRef.current = [];

    // Add garage markers
    garages.forEach((garage, index) => {
      if (!garage.latitude || !garage.longitude) return;

      let marker;
      
      if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
        // Use AdvancedMarkerElement
        const markerContent = document.createElement('div');
        markerContent.innerHTML = `
          <div style="
            width: 32px; 
            height: 40px; 
            background: #f44336; 
            border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
            position: relative;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 12px;
          ">${index + 1}</div>
        `;
        
        marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat: parseFloat(garage.latitude), lng: parseFloat(garage.longitude) },
          map: mapInstanceRef.current,
          title: garage.name,
          content: markerContent
        });
      } else {
        // Fallback to regular Marker
        marker = new window.google.maps.Marker({
          position: { lat: parseFloat(garage.latitude), lng: parseFloat(garage.longitude) },
          map: mapInstanceRef.current,
          title: garage.name,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40S32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="#f44336"/>
                <circle cx="16" cy="16" r="8" fill="white"/>
                <text x="16" y="20" text-anchor="middle" fill="#f44336" font-size="10" font-weight="bold">${index + 1}</text>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 40),
            anchor: new window.google.maps.Point(16, 40)
          }
        });
      }

      // Add click listener to marker
      marker.addListener('click', () => {
        setSelectedGarage(garage);
        showInfoWindow(marker, garage);
      });

      markersRef.current.push(marker);
    });

    // Adjust map bounds to fit all markers
    if (markersRef.current.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      if (center && center.latitude && center.longitude) {
        bounds.extend({ lat: center.latitude, lng: center.longitude }); // Include user location
      }
      
      markersRef.current.forEach(marker => {
        if (marker.position) {
          bounds.extend(marker.position);
        } else if (marker.getPosition) {
          bounds.extend(marker.getPosition());
        }
      });
      
      mapInstanceRef.current.fitBounds(bounds);
      
      // Ensure minimum zoom level
      const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'idle', () => {
        if (mapInstanceRef.current.getZoom() > 15) {
          mapInstanceRef.current.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  };

  const showInfoWindow = (marker, garage) => {
    const content = `
      <div style="width: 100%; max-width: 320px; min-width: 250px; padding: 12px; box-sizing: border-box;">
        <h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: bold; color: #333; text-align: left;">
          ${garage.name}
        </h3>
        <div style="margin-bottom: 16px;">
          <div style="display: flex; align-items: center; margin-bottom: 6px; font-size: 14px; color: #666;">
            <span style="margin-right: 6px;">📍</span>
            <span>${garage.address}, ${garage.postcode}</span>
          </div>
          <div style="display: flex; align-items: center; margin-bottom: 6px; font-size: 14px; color: #666;">
            <span style="margin-right: 6px;">🧭</span>
            <span>${formatDistance(garage.distance_miles)} away</span>
          </div>
          ${garage.rating ? `
            <div style="display: flex; align-items: center; margin-bottom: 6px; font-size: 14px; color: #666;">
              <span style="margin-right: 6px;">⭐</span>
              <span>${garage.rating} (${garage.total_reviews || 0} reviews)</span>
            </div>
          ` : ''}
        </div>
        
        <div style="padding-top: 8px;">
          <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 12px;">
            <div style="font-size: 32px; font-weight: bold; color: #1976d2; line-height: 1; text-align: left;">
              ${formatPrice(garage.total_price)}
            </div>
            <button 
              onclick="window.selectGarage('${garage.id}')" 
              style="
                background: #1976d2; 
                color: white; 
                border: none; 
                padding: 12px 32px; 
                border-radius: 4px; 
                cursor: pointer; 
                font-size: 16px;
                font-weight: 500;
                white-space: nowrap;
                min-width: 120px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                transition: all 0.2s ease;
                display: block;
                position: relative;
                z-index: 1000;
              "
              onmouseover="this.style.background='#1565c0'; this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)'"
              onmouseout="this.style.background='#1976d2'; this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)'"
              onmousedown="this.style.transform='translateY(0)'"
            >
              Select
            </button>
          </div>
        </div>
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(mapInstanceRef.current, marker);
  };

  // Expose garage selection to global scope for InfoWindow button
  useEffect(() => {
    window.selectGarage = (garageId) => {
      const garage = garages.find(g => g.id === garageId);
      if (garage) {
        onGarageSelect(garage);
      }
    };

    return () => {
      delete window.selectGarage;
    };
  }, [garages, onGarageSelect]);

  // Error state
  if (mapError || !import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
    return (
      <Box sx={{ 
        height: '70vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.100',
        borderRadius: 2
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Map View Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!import.meta.env.VITE_GOOGLE_MAPS_API_KEY 
              ? 'Google Maps API key required for map functionality'
              : 'Unable to load Google Maps. Please try refreshing the page.'
            }
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '70vh', position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '8px'
        }}
      />
      
      {/* Loading overlay */}
      {!mapLoaded && !mapError && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 2
        }}>
          <Typography variant="h6" color="text.secondary">
            Loading map...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GoogleMapComponent;